import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AnalyticsPeriod,
  TangramMetrics,
  MyTangramRow,
  TrendDelta,
} from '../../models/analytics.types';
import {
  ProfessorAnalyticsService,
  OverviewListCriteria,
} from '../../services/professor-analytics.service';

@Component({
  selector: 'tangram-pa-overview-tab',
  templateUrl: './overview-tab.component.html',
  styleUrls: ['./overview-tab.component.scss']
})
export class OverviewTabComponent implements OnInit, OnChanges {
  // Period flows down from the parent so the dashboard's period dropdown
  // refetches all tabs in lockstep.
  @Input() period: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  loading = true;
  error = '';

  myTangramsCount = 0;
  tangramMetrics: TangramMetrics = { views: 0, uniqueUsers: 0, completionRate: 0, avgTimeSpent: '0m' };
  // Both lists are now server-computed per shared criteria so the UI
  // doesn't second-guess what counts as "top" or "attention".
  topTangrams: MyTangramRow[] = [];
  lowTangrams: MyTangramRow[] = [];
  trends: { [key: string]: TrendDelta } = {};
  criteria: OverviewListCriteria = {
    minViewsForRanking: 5,
    topCompletionThreshold: 70,
    attentionCompletionThreshold: 40,
    listSize: 3,
  };

  private fetchSub?: Subscription;

  constructor(private analyticsService: ProfessorAnalyticsService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['period'] || changes['startDate'] || changes['endDate']) &&
        !(changes['period'] && changes['period'].firstChange)) {
      this.load();
    }
  }

  trend(key: string): TrendDelta {
    return this.trends[key] || { delta: 0, unit: '' };
  }

  private load(): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    this.loading = true;
    this.error = '';

    // The `/overview` endpoint already returns top/low rankings using the
    // same logic the Tangram Metrics tab uses (raw top/bottom by
    // completion rate, with a fallback to "any tangram with views" when
    // no tangram has crossed the data-volume gate yet) — see
    // buildTangramMetricsRankings in the backend's analytics service.
    // So a single fetch covers everything this tab needs.
    this.fetchSub = this.analyticsService.getOverviewAnalytics(this.period, this.startDate, this.endDate).subscribe(
      (data) => {
        this.tangramMetrics = data.tangramMetrics;
        // Counts every non-draft, non-deleted pathway, matching the
        // My Tangrams "Published" chip. The /overview endpoint no longer
        // ships the per-tangram list — this count is the only thing the
        // KPI tile needs.
        this.myTangramsCount = data.publishedTangramsCount || 0;
        this.trends = data.trends || {};
        // Use the server-computed lists directly. The criteria they were
        // selected by come back in the response so the empty-state copy
        // can reference the actual thresholds.
        this.topTangrams = data.topPerformers || [];
        this.lowTangrams = data.needsAttention || [];
        if (data.criteria) {
          this.criteria = data.criteria;
        }
        // Publish to the per-tab export cache so the dashboard's top-right
        // Export button can build a CSV from this tab's live data.
        this.analyticsService.setLatestTabData('overview', data);
        this.loading = false;
      },
      (err) => {
        this.error = (err && err.message) ? err.message : 'Failed to load overview analytics.';
        this.loading = false;
      },
    );
  }
}
